import { watch } from 'vue';
import lodash from "lodash"
import { useForm as createUseForm } from '@ant-design-vue/use';
import i18n from '../../../../../i18n';
import { required, requiredWhen } from '../../../../../utils/validate';
import { createLoading } from '../../../../../utils/loading';
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createRowActionEdit,
    createRowActionView
} from '../../../../../utils/page';
import {
    checkAttribute,
    initUseForm,
    onClickActiveAll,
    onClickArchiveAll,
    onClickCreate,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from '../../../../../utils/form';
import { setAppPageHeader } from '../../../../../components/layout/app-layout/app-layout.logic';

import {
    callActiveAll,
    callArchiveAll,
    callDeleteAll,
    callLookupStatus,
    callSearch,
    createAppPageHeaderDefault,
    createFields,
    createFieldsAnalysis,
    createSearchOneCriteria,
    onSearch,
    initState, createActionButtons
} from '../../sampling-product-analysis/analysis.logic';

import {
    createFieldInputNumber,
    createFieldSelect, createFieldTextArea
} from '../../../../../components/input/template-builder/template-builder.logic';
import { createColumnInput } from '../../../../../components/table/c-table.logic';
import store from '../../../../../store/index';
import { createConditionForm } from './physical-analysis-topic.condition';

export const callAcceptStatus = ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
    group: 'ACCEPT_STATUS',
    value: searchQuery
});

export const callRepeatOption = ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
    group: 'LAB_REPEAT_OPTION',
    value: searchQuery
});

export const callProductPackagingUnit = ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
    group: 'PRODUCT_PACKAGING_UNIT',
    value: searchQuery
});

export const actionPrefix = 'resource/v1/transaction/samplingProductAnalysis';
export const routerPrefix = 'lab-physic-sampling-product-analysis-exports';
export const routerLabAnalysisTopicPrefix = 'lab-physic-sampling-product-analysis-exports';
export const { isLoading, setIsLoading } = createLoading();

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => initUseForm({ data, page }))
        .then(() => loadDefaultForm({ props, data }))
        .finally(() => setIsLoading(false));
};


export const loadDefaultForm = ({ data }) => {
    const promiseAll = [callLookupStatus(), callAcceptStatus({}), callProductPackagingUnit({}), callRepeatOption({})];
    return Promise.all(promiseAll).then(arr => {
        data.statuses = arr[0];
        data.acceptStatus = arr[1];
        data.useForm.modelRef.refAmountUnit = arr[2].filter(it => it.key == 'Pcs')[0];
        data.repeatOption = arr[3];
    });
};


export const createForm = () => {
    return {
        refSamplingProduct: null,
        refLabType: null,
        samplingReceiveDate: null,
        analysisStartDate: null,
        analysisEndDate: null,
        analysisEn: null,
        reviewDate: null,
        approveDate: null,
        refReviewer: null,
        refAuditor: null,
        remark: null,
        refDepartment: null,
        refLabExt: null,
        refParent: null,
        isAccepted: null,
        refLabAnalysisStatus: null,
        refLabResultStatus: null,
        rejectRemark: null,
        netWeight: null,
        refNetWeightUnit: null,
        netVolume: null,
        refNetVolumeUnit: null,
        drainWeight: null,
        refDrainWeightUnit: null,
        samplingAmount: null,
        amount: null,
        refAmountUnit: null,
        children: [],
        topics: [],
        transferTos: [],
        isActive: true
    };
};
export const createRules = ({ data }) => {
    return {
        refSamplingProduct: [],
        refLabType: [],
        samplingReceiveDate: [required],
        analysisStartDate: [required],
        analysisEn: [],
        reviewDate: [],
        approveDate: [],
        refReviewer: [],
        refAuditor: [],
        remark: [],
        refDepartment: [],
        refLabExt: [],
        refParent: [],
        isAccepted: [],
        refLabAnalysisStatus: [],
        refLabResultStatus: [],
        rejectRemark: [requiredWhen({ when: () => data.useForm?.modelRef?.refLabResultStatus?.key == 'Reject' || data.useForm?.modelRef?.refLabResultStatus?.key == 'RejectCanRepeat' })],
        netWeight: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'NW' }) })],
        refNetWeightUnit: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'NW' }) })],
        netVolume: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'NV' }) })],
        refNetVolumeUnit: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'NV' }) })],
        drainWeight: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'DW' }) })],
        refDrainWeightUnit: [requiredWhen({ when: () => checkRequireWeight({ data, key: 'DW' }) })],
        samplingAmount: [required],
        amount: [],
        refAmountUnit: [required],
        children: [],
        topics: [],
        transferTos: []
    };
};

export const checkRequireWeight = ({ data, key }) => {
    if (data?.useForm?.modelRef && !data.viewOnly) {
        if (data?.isValidateNW && key == 'NW') {
            const existNw = data?.useForm?.modelRef?.topics.find(it => (it.isActive !== false && it.isDelete !== true) && it.refLabTopic.nameS == 'NW');
            return existNw;
        }
        if (data?.isValidateNV && key == 'NV') {
            const existNv = data?.useForm?.modelRef?.topics.find(it => (it.isActive !== false && it.isDelete !== true) && it.refLabTopic.nameS == 'NV');
            return existNv;
        }
        if (data?.isValidateDW && key == 'DW') {
            const existDW = data?.useForm?.modelRef?.topics.find(it => (it.isActive !== false && it.isDelete !== true) && it.refLabTopic.nameS == 'DW');
            return existDW;
        }
    }

};

export const createFieldsPhysicAnalysisResult = ({ props, data }) => {
    const { viewOnly } = props;
    const { useForm, acceptStatus } = data;
    const { modelRef } = useForm;
    return [
        createFieldTextArea({
            key: 'remark',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 4, md: 4 }, wrapperCol: { xs: 20, md: 20 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.remarkEn'),
            inputProp: () => ({
                value: modelRef.remark,
                'onUpdate:value': value => modelRef.remark = value,
                disabled: viewOnly
            })
        }),
        createFieldSelect({
            key: 'refLabAnalysisStatus',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 4, md: 4 }, wrapperCol: { xs: 20, md: 4 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.refLabResultStatus'),
            inputProp: () => ({
                value: modelRef.refLabResultStatus,
                'onUpdate:value': value => {
                    modelRef.refLabResultStatus = value;
                    calResultAnalysisTopic({ useForm, acceptStatus });
                },
                disabled: true,
                list: callAcceptStatus,
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.samplingProductAnalysis.refLabResultStatus')
            }),
            changeLog: { getValue: ({ record }) => record.refLabResultStatus?.value }
        }),
        createFieldTextArea({
            visible: () => modelRef.refLabResultStatus?.key == 'Reject' || modelRef.refLabResultStatus?.key == 'RejectCanRepeat',
            key: 'rejectRemark',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 4, md: 4 }, wrapperCol: { xs: 20, md: 20 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.rejectRemarkEn'),
            inputProp: () => ({
                value: modelRef.rejectRemark,
                'onUpdate:value': value => modelRef.rejectRemark = value,
                disabled: viewOnly
            })
        })
    ];
};

export const calResultAnalysisTopic = ({ useForm, acceptStatus }) => {
    const { modelRef } = useForm;
    const activeItems = modelRef.topics.filter(d => d.isDelete !== true);
    const allAcceptTopic = activeItems.map(({ refTopicAnalysisStatus }) => refTopicAnalysisStatus?.key).every((key) => key == 'Accept');
    const rejectTopic = activeItems.filter(item => item?.refTopicAnalysisStatus?.key == 'Reject');
    const allRejectTopicAndCanRepeat = rejectTopic.every((item) => item?.refTopicRepeatOption.key === 'Repeatable') && rejectTopic.length;
    // check length with every because when empty array every function will return true but real should return false
    const someRejectTopicAndCanNotRepeat = rejectTopic.some(item => item?.refTopicRepeatOption.key === 'Unrepeatable');
    if (allAcceptTopic) {
        modelRef.refLabResultStatus = acceptStatus.filter(it => it.key == 'Accept')[0];
    } else if (allRejectTopicAndCanRepeat) {
        modelRef.refLabResultStatus = acceptStatus.filter(it => it.key == 'RejectCanRepeat')[0];
    } else if (someRejectTopicAndCanNotRepeat) {
        modelRef.refLabResultStatus = acceptStatus.filter(it => it.key == 'Reject')[0];
    } else {
        modelRef.refLabResultStatus = null;
    }


};

export const createFieldsPhysicAnalysis = ({ props, data }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const fieldsAnalysis = createFieldsAnalysis({ props, data });
    return [
        ...fieldsAnalysis,
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => <hr/>
        },
        createFieldInputNumber({
            key: `samplingAmount`,
            colProp: () => ({ xs: 24, md: 8 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 12, md: 14 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.samplingAmount'),
            inputProp: () => ({
                value: modelRef.samplingAmount,
                'onUpdate:value': value => modelRef.samplingAmount = value,
                disabled: viewOnly
            })
        }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => {
            }
        },
        createFieldInputNumber({
            key: `netWeight`,
            colProp: () => ({ xs: 24, md: 7 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 12, md: 12 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.netWeight'),
            inputProp: () => ({
                value: modelRef.netWeight,
                'onUpdate:value': value => modelRef.netWeight = value,
                disabled: viewOnly,
                allowPrecision: true
            })
        }),
        createFieldSelect({
            key: 'refNetWeightUnit',
            colProp: () => ({ xs: 24, md: 3 }),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 24 } }),
            inputProp: () => ({
                value: modelRef.refNetWeightUnit,
                'onUpdate:value': value => modelRef.refNetWeightUnit = value,
                disabled: viewOnly,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_PHYSIC_UNIT',
                    value: searchQuery,
                    valueEn: searchQuery
                }).then(list => { return list.filter(it => lodash.get(it.attribute, 'isNW'))}),
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.lookup.modelGroup.LAB_PHYSIC_UNIT')
            }),
            changeLog: { getValue: ({ record }) => record.refNetWeightUnit?.value }
        }),
        ...createFieldsConvertUnit({ props, data, amountKey: 'netWeight', unitKey: 'refNetWeightUnit' }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => {
            }
        },
        createFieldInputNumber({
            key: `netVolume`,
            colProp: () => ({ xs: 24, md: 7 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 12, md: 12 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.netVolume'),
            inputProp: () => ({
                value: modelRef.netVolume,
                'onUpdate:value': value => modelRef.netVolume = value,
                disabled: viewOnly
            })
        }),
        createFieldSelect({
            key: 'refNetVolumeUnit',
            colProp: () => ({ xs: 24, md: 3 }),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 24 } }),
            inputProp: () => ({
                value: modelRef.refNetVolumeUnit,
                'onUpdate:value': value => modelRef.refNetVolumeUnit = value,
                disabled: viewOnly,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_PHYSIC_UNIT',
                    value: searchQuery,
                    valueEn: searchQuery
                }).then(list => { return list.filter(it => lodash.get(it.attribute, 'isNV'))}),
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.lookup.modelGroup.LAB_PHYSIC_UNIT')
            }),
            changeLog: { getValue: ({ record }) => record.refNetVolumeUnit?.value }
        }),
        ...createFieldsConvertUnit({ props, data, amountKey: 'netVolume', unitKey: 'refNetVolumeUnit' }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => {
            }
        },
        createFieldInputNumber({
            key: `drainWeight`,
            colProp: () => ({ xs: 24, md: 7 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 12, md: 12 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.drainWeight'),
            inputProp: () => ({
                value: modelRef.drainWeight,
                'onUpdate:value': value => modelRef.drainWeight = value,
                disabled: viewOnly
            })
        }),
        createFieldSelect({
            key: 'refDrainWeightUnit',
            colProp: () => ({ xs: 24, md: 3 }),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 24 } }),
            inputProp: () => ({
                value: modelRef.refDrainWeightUnit,
                'onUpdate:value': value => modelRef.refDrainWeightUnit = value,
                disabled: viewOnly,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_PHYSIC_UNIT',
                    value: searchQuery,
                    valueEn: searchQuery
                }).then(list => { return list.filter(it => lodash.get(it.attribute, 'isDW'))}),
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.lookup.modelGroup.LAB_PHYSIC_UNIT')
            }),
            changeLog: { getValue: ({ record }) => record.refDrainWeightUnit?.value }
        }),
        ...createFieldsConvertUnit({ props, data, amountKey: 'drainWeight', unitKey: 'refDrainWeightUnit' }),
        createFieldInputNumber({
            key: `amount`,
            colProp: () => ({ xs: 24, md: 7 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 12, md: 12 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.amount'),
            inputProp: () => ({
                value: modelRef.amount,
                'onUpdate:value': value => modelRef.amount = value,
                disabled: viewOnly
            })
        }),
        createFieldSelect({
            key: 'refAmountUnit',
            colProp: () => ({ xs: 24, md: 3 }),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 24 } }),
            inputProp: () => ({
                value: modelRef.refAmountUnit,
                'onUpdate:value': value => modelRef.refAmountUnit = value,
                disabled: true,
                list: callProductPackagingUnit,
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.lookup.modelGroup.LAB_PHYSIC_UNIT'),
                optLabel: 'value'
            }),
            changeLog: { getValue: ({ record }) => record.refAmountUnit?.value }
        })
    ];
};

export const createFieldsConvertUnit = ({ data, amountKey, unitKey }) => {
    const { useForm } = data;
    const { modelRef } = useForm;

    return [
        createFieldInputNumber({
            colProp: () => ({ xs: 24, md: 6 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 12, md: 12 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.labels.convertUnit'),
            inputProp: () => {
                const base = modelRef[unitKey]?.attribute?.base;
                return {
                    value: modelRef[unitKey] && modelRef[amountKey] ? modelRef[amountKey] * (base || 1) : null,
                    disabled: true,
                    comma: false,
                    allowPrecision: true,
                    maxPrecision: 3
                };
            }
        }),
        createFieldSelect({
            colProp: () => ({ xs: 24, md: 3 }),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 24 } }),
            inputProp: () => ({
                value: modelRef[unitKey]?.refParent ? modelRef[unitKey]?.refParent : modelRef[unitKey],
                disabled: true,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_PHYSIC_UNIT',
                    value: searchQuery,
                    valueEn: searchQuery
                }),
                renderLabel: ({ item }) => <div>{item.value}</div>,
                placeholder: i18n.global.t('model.lookup.modelGroup.LAB_PHYSIC_UNIT')
            })
        })
    ];
};

export const createColumnsAnalysisTopic = () => {
    return [
        createColumnInput({
            title: () => i18n.global.t('model.samplingAnalysisTopic.samplingNo'),
            dataIndex: 'refSamplingProduct.samplingNo'
        }),
        createColumnInput({
            title: () => i18n.global.t('model.samplingAnalysisTopic.refTopicStatus'),
            dataIndex: 'refSamplingProduct.samplingNo'
        })
    ];
};
export const initProductAnalysisTopicActions = ({ props, data, state }) => {
    const { menuContext } = props;
    data.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({
                rowSelection,
                callActiveAll,
                state,
                onSearch,
                setIsLoading
            })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) => onClickArchiveAll({
                rowSelection,
                callArchiveAll,
                state,
                onSearch,
                setIsLoading
            })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({
                rowSelection,
                callDeleteAll,
                state,
                onSearch,
                setIsLoading
            })
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [createManageExtraCreate({
        menuContext,
        onClick: () => onClickCreate({ routerLabAnalysisTopicPrefix })
    })];
    data.rowActions = [
        createRowActionView({
            menuContext,
            onClick: ({ record }) => onClickView({ record, routerLabAnalysisTopicPrefix })
        }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerLabAnalysisTopicPrefix })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data });
    data.form = createForm();
    data.columnsAnalysisTopic = createColumnsAnalysisTopic({ props, data });
    data.rules = createRules({ data });
    data.useForm = createUseForm(data.form, data.rules);
    data.conditionForms = createConditionForm({ props, data, refs });
    data.fieldsSampling = createFields({ props, data });
    data.fieldsAnalysis = createFieldsPhysicAnalysis({ props, data });
    data.fieldsResult = createFieldsPhysicAnalysisResult({ props, data });
    data.actionButtons = createActionButtons({ props, data });

    const { useForm } = data;
    const { modelRef } = useForm;


    // watch(
    //     () => modelRef.samplingAmount,
    //     () => {
    //         modelRef.topics.forEach(topic => {
    //             if (topic.refLabTopic.refLabFormula.key == "Weight") {
    //                 if  (topic.inputs) {
    //                     setAnalysisInput({ form: topic, samplingAmount: modelRef.samplingAmount });
    //                 }
    //             }
    //         });
    //     },
    //     { flush: 'post' }
    // );

    watch(
        () => modelRef.topics.filter(d => d.isDelete !== true && d.isActive == true).length,
        () => {
            const activeItems = modelRef.topics.filter(d => d.isDelete !== true);
            if (activeItems.length == 0) {
                modelRef.refLabResultStatus = null;
            }
        }
    );

    initState({ props, data }).then(state => {
        initProductAnalysisTopicActions({ props, data, state });
        loadForm({ props, data })
            .then(() => {
                data.fetchCriteriaAnalysisTopic = {
                    criteria: {
                        select: [
                            '**',
                            'refLabTopic.refLabFormula.*'
                        ],
                        size: 20,
                        where: [{
                            name: 'refSamplingProductAnalysis.id',
                            type: 'UUID',
                            operator: '=',
                            value: data.useForm.modelRef.id
                        }],
                        sort: [{ name: 'refLabTopic.seq', type: 'ASC' }],
                        children: [
                            {
                                name: 'transferTos', page: 0, size: 10000, select: [
                                    '**',
                                    'refLabTopic.*',
                                    'refLabTopic.refLabFormula.*',
                                    'refSamplingProductAnalysis.refDepartment.*',
                                    'refSamplingProductAnalysis.*',
                                    'refSamplingProductAnalysis.refLabAnalysisStatus.*',
                                    'refSamplingProductAnalysis.refDepartment.*',
                                    'refSamplingProductAnalysis.refLabExt.*',
                                    'refSamplingProductAnalysis.refLabType.*',
                                    'refSamplingProductAnalysis.refExtCenterType.*'
                                ]
                            }
                        ]
                    }
                };
                data.childFormMenuContext = { attribute: ['canView', 'canCreate', 'canEdit', 'canDelete'] };
                const routerPrefixCustom = (data.useForm.modelRef?.refSamplingProduct?.refSamplingRequest?.refSamplingRequestType?.key === "M") ? "lab-physic-sampling-product-analysis-monitors" : routerPrefix ;
                if (props.setAppPageHeader) {
                    setAppPageHeader({
                        slots: {
                            top: () => createAppPageHeaderTop({
                                props,
                                data,
                                state,
                                routerPrefix: routerPrefixCustom,
                                onClickView,
                                onClickEdit
                            }),
                            default: () => createAppPageHeaderDefault({ props, data })
                        }
                    });
                }
            });
    });
};

